/**
 * Status of the Azure Functions (jobsStatus collection), refreshed every 30
 * seconds. Admin only.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES, type Platform } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { jobStatusesQueryOptions } from './api';
import { formatLaunchDate, statusIcon } from './format';

export function InterfacePage({
  platform = PLATFORM.ABCDIOR,
}: Readonly<{
  platform?: Platform;
}>) {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const {
    data: items = [],
    isFetching,
    isError,
    error,
  } = useQuery({ ...jobStatusesQueryOptions(), enabled: isAdmin });

  useEffect(() => {
    if (isError) {
      console.error(error);
      toast.error('Failed to load the items, please try again later');
    }
  }, [isError, error]);

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu platform={platform} />
      <div className="summary-table my-12">
        <div className="table-container">
          <div
            className="items-container"
            role="region"
            aria-label="Azure functions"
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Status</th>
                  <th style={{ width: '35%' }}>Name</th>
                  <th style={{ width: '35%' }}>Last launch date</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr
                    key={
                      item.id ??
                      item._id ??
                      `${item.function ?? 'job'}-${index}`
                    }
                    className="store-row"
                  >
                    <td
                      style={{ width: '15%' }}
                      title={item.status ?? undefined}
                      data-testid="interface-row-status"
                    >
                      <FontAwesomeIcon
                        icon={statusIcon(item.status)}
                        className="action-icon"
                        aria-hidden="true"
                      />
                    </td>
                    <td
                      style={{ width: '35%' }}
                      title={item.function ?? undefined}
                      data-testid="interface-row-name"
                    >
                      {item.function}
                    </td>
                    <td
                      style={{ width: '35%' }}
                      title={formatLaunchDate(item.lastLaunchDate)}
                      data-testid="interface-row-launch-date"
                    >
                      {formatLaunchDate(item.lastLaunchDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isFetching && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon pointer-cursor"
          aria-label="Loading"
          role="status"
        />
      )}
    </div>
  );
}
